// src/components/modals/DetailsModal/SentenceModalTabs/PolitenessTab.js
import React, { useState, useRef, useEffect } from 'react';
import { ChatBubbleLeftRightIcon, BookOpenIcon } from '@heroicons/react/24/outline';
import GlassyIcon from '../../../common/GlassyIcon';
import GlassyBadge from '../../../common/GlassyBadge';

const politenessLevels = {
  casual: {
    name: 'Casual',
    description: 'Informal speech used with friends, family, and people of equal or lower status.',
    color: 'var(--color-politeness-casual)',
  },
  polite: {
    name: 'Polite',
    description: 'Standard polite form used in most social situations and with strangers.',
    color: 'var(--color-politeness-polite)',
  },
  humble: {
    name: 'Humble',
    description: "Respectful language that lowers the speaker's status to show respect.",
    color: 'var(--color-politeness-humble)',
  },
  honorific: {
    name: 'Honorific',
    description: 'Language that elevates the listener to show maximum respect.',
    color: 'var(--color-politeness-honorific)',
  },
};

const PolitenessTab = ({ politeness }) => {
  const [showGuide, setShowGuide] = useState(false);
  const [isGuideAnimating, setIsGuideAnimating] = useState(false);
  const guideRef = useRef(null);
  const currentLevel = politenessLevels[politeness?.toLowerCase()] || null;

  useEffect(() => {
    if (showGuide && guideRef.current && !isGuideAnimating) {
      // Wait for the expansion animation to complete, then check scroll position
      const timer = setTimeout(() => {
        if (guideRef.current) {
          const rect = guideRef.current.getBoundingClientRect();
          const isFullyVisible = rect.bottom <= window.innerHeight;

          if (!isFullyVisible) {
            guideRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'end',
            });
          }
        }
      }, 50); // Wait for the expansion animation to complete

      return () => clearTimeout(timer);
    }
  }, [showGuide, isGuideAnimating]);

  const handleShowGuide = () => {
    setIsGuideAnimating(true);
    // Small delay to trigger height recalculation
    setTimeout(() => {
      setShowGuide(true);
      // Allow animation to complete
      setTimeout(() => {
        setIsGuideAnimating(false);
      }, 350); // Match the CSS transition duration
    }, 10);
  };

  const handleHideGuide = () => {
    setIsGuideAnimating(true);
    setShowGuide(false);
    // Allow animation to complete
    setTimeout(() => {
      setIsGuideAnimating(false);
    }, 350);
  };

  return (
    <div className="relative p-6">
      {politeness ? (
        <>
          {/* Background bubbles - left and right */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {/* Right side bubbles */}
            <div
              className="absolute rounded-full backdrop-blur-sm border border-white/5"
              style={{
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.06), rgba(59, 130, 246, 0.04))',
                width: '85px',
                height: '85px',
                top: '10%',
                right: '8%',
                boxShadow: '0 8px 32px rgba(139, 92, 246, 0.02)',
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-30 rounded-full" />
            </div>
            <div
              className="absolute rounded-full backdrop-blur-sm border border-white/5"
              style={{
                background: 'linear-gradient(45deg, rgba(236, 72, 153, 0.05), rgba(139, 92, 246, 0.03))',
                width: '70px',
                height: '70px',
                bottom: '25%',
                right: '12%',
                boxShadow: '0 8px 32px rgba(236, 72, 153, 0.01)',
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-30 rounded-full" />
            </div>

            {/* Left side bubbles */}
            <div
              className="absolute rounded-full backdrop-blur-sm border border-white/5"
              style={{
                background: 'linear-gradient(225deg, rgba(34, 197, 94, 0.05), rgba(59, 130, 246, 0.03))',
                width: '75px',
                height: '75px',
                top: '20%',
                left: '8%',
                boxShadow: '0 8px 32px rgba(34, 197, 94, 0.01)',
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-30 rounded-full" />
            </div>
            <div
              className="absolute rounded-full backdrop-blur-sm border border-white/5"
              style={{
                background: 'linear-gradient(315deg, rgba(59, 130, 246, 0.04), rgba(236, 72, 153, 0.03))',
                width: '60px',
                height: '60px',
                bottom: '15%',
                left: '12%',
                boxShadow: '0 8px 32px rgba(59, 130, 246, 0.01)',
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-30 rounded-full" />
            </div>
          </div>

          {/* Content */}
          <div className="relative">
            {/* Main politeness display - centered */}
            <div className="text-center py-8">
              <div className="flex items-center justify-center mb-4">
                <GlassyIcon
                  icon={ChatBubbleLeftRightIcon}
                  size="md"
                  color="purple"
                  className="mr-3"
                />
                <h3 className="text-lg font-semibold text-content-primary">This sentence uses</h3>
              </div>

              {currentLevel && (
                <GlassyBadge
                  politeness={currentLevel.name.toLowerCase()}
                  size="lg"
                  className="text-xl font-bold px-6 py-3"
                >
                  {currentLevel.name}
                </GlassyBadge>
              )}

              <p className="text-lg text-content-primary mt-3">speech</p>
            </div>

            {/* Guide button - bottom right */}
            {!showGuide && (
              <div className="absolute bottom-0 right-0">
                <button
                  onClick={handleShowGuide}
                  disabled={isGuideAnimating}
                  className="
                    relative p-3 rounded-xl backdrop-blur-sm border border-border overflow-hidden
                    transition-all duration-200 group hover:shadow-lg hover:-translate-y-1
                    animate-bounce shadow-lg disabled:animate-none disabled:opacity-50
                  "
                  style={{
                    background: 'linear-gradient(135deg, var(--color-secondary), var(--color-accent))',
                    boxShadow: '0 4px 16px var(--color-secondary)30',
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-60" />
                  <BookOpenIcon className="h-6 w-6 text-white drop-shadow-sm relative z-10" />
                </button>
              </div>
            )}

            {/* Guide content with smooth height transition */}
            <div
              className={`
                overflow-hidden transition-all duration-300 ease-in-out
                ${showGuide ? 'max-h-screen opacity-100 mt-8' : 'max-h-0 opacity-0 mt-0'}
              `}
            >
              <div ref={guideRef} className="pt-6 border-t border-border">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-content-primary">Japanese Politeness Levels</h4>
                  <button
                    onClick={handleHideGuide}
                    disabled={isGuideAnimating}
                    className="text-sm text-content-secondary hover:text-content-primary transition-colors disabled:opacity-50"
                  >
                    Hide Guide
                  </button>
                </div>
                <div className="space-y-3">
                  {Object.entries(politenessLevels).map(([key, level]) => {
                    const isCurrentLevel = key === politeness?.toLowerCase();
                    return (
                      <div
                        key={key}
                        className={`
                          p-4 rounded-xl border transition-all duration-200
                          ${isCurrentLevel ? 'border-primary bg-primary/5' : 'border-border bg-background-primary'}
                        `}
                      >
                        <div className="flex items-center mb-2">
                          <div className="w-3 h-3 rounded-full mr-3" style={{ backgroundColor: level.color }} />
                          <span className={`font-semibold ${isCurrentLevel ? 'text-primary' : 'text-content-primary'}`}>{level.name}</span>
                          {isCurrentLevel && (
                            <GlassyBadge
                              color="orange"
                              size="sm"
                              variant="default"
                              className="ml-2"
                            >
                              Current
                            </GlassyBadge>
                          )}
                        </div>
                        <p className="text-sm text-content-secondary leading-relaxed">{level.description}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-8">
          <ChatBubbleLeftRightIcon className="h-12 w-12 text-content-secondary/40 mx-auto mb-3" />
          <p className="text-content-secondary">Politeness level information is not available.</p>
        </div>
      )}
    </div>
  );
};

export default PolitenessTab;
